/**
 * NorseMobileControls - Norse-styled mobile controls for Midgard
 * 
 * Uses ecctrl's Joystick and VirtualButton for movement with custom Norse styling.
 * 
 * Layout:
 * - Left: Joystick (dark timber/leather with bronze border)
 * - Right bottom: Sprint, Jump, Action buttons
 * - Right middle: PTT button (separate integration)
 * - Above controls: Contextual actions (Enter/Sit/Talk)
 * 
 * Styling:
 * - Dark timber/leather feel
 * - Bronze/aged-gold borders
 * - Restrained green/amber highlights
 * - Clear pressed/active states
 * - Safe-area aware
 * - Portrait + landscape responsive
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Joystick, VirtualButton, useJoystickStore, useButtonStore } from 'ecctrl/input';
import { Zap, ArrowUp, Crosshair, Hand, Mic, MicOff, Radio, Loader } from 'lucide-react';
import './NorseMobileControls.css';

// Virtual button ids — must match the ids EcctrlPlayer reads from useButtonStore
const BTN_JUMP = 'jump';
const BTN_RUN = 'run';
const BTN_ACTION = 'action';

const NORSE_BTN_WRAPPER = {
  position: 'relative',
  height: '52px',
  width: '52px',
  background: 'rgba(20,14,8,0.6)',
  border: '2px solid rgba(190,150,80,0.55)',
};

const NORSE_BTN_CAP = {
  width: '40px',
  height: '40px',
  background: 'rgba(38,28,18,0.9)',
  fontSize: '10px',
  letterSpacing: '0.05em',
};

/**
 * NorseMobileControls Component
 * 
 * Props:
 * - currentInteraction: Active interaction object
 * - onInteract: Callback when interaction triggered
 * - voiceState: 'disconnected' | 'connecting' | 'connected' | 'reconnecting'
 * - isPTTActive: Whether PTT is currently active
 * - onPTTStart/End: PTT callbacks
 * - micMuted: Whether mic is muted
 * - showPTT: Whether to show PTT button
 * - disabled: Disable all controls (menu open)
 */
export default function NorseMobileControls({
  currentInteraction,
  onInteract,
  voiceState = 'disconnected',
  isPTTActive = false,
  onPTTStart,
  onPTTEnd,
  micMuted = false,
  showPTT = true,
  disabled = false,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  
  // Detect mobile and orientation
  useEffect(() => {
    const checkMobile = () => {
      const mobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(mobile);
    };
    
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    
    checkMobile();
    checkOrientation();
    
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);
  
  // Prevent default touch behaviors on control area
  useEffect(() => {
    if (!isMobile) return;
    
    const preventScroll = (e) => {
      if (e.target.closest('[data-norse-control]')) {
        e.preventDefault();
      }
    };
    
    document.body.addEventListener('touchmove', preventScroll, { passive: false });
    return () => document.body.removeEventListener('touchmove', preventScroll);
  }, [isMobile]);
  
  // ========== INTERACT HANDLER ==========
  const handleInteract = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentInteraction && onInteract) {
      onInteract(currentInteraction);
    }
  }, [currentInteraction, onInteract]);
  
  // ========== PTT HANDLERS ==========
  const handlePTTStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onPTTStart?.();
  }, [onPTTStart]);
  
  const handlePTTEnd = useCallback((e) => {
    e.preventDefault();
    onPTTEnd?.();
  }, [onPTTEnd]);
  
  // Don't render on desktop
  if (!isMobile) return null;
  
  const isTransmitting = isPTTActive;
  const isConnected = voiceState === 'connected';
  const isConnecting = voiceState === 'connecting' || voiceState === 'reconnecting';
  
  return (
    <>
      {/* Ecctrl Joystick for movement - positioned bottom-left */}
      {!disabled && (
        <div 
          className="fixed z-40 pointer-events-auto"
          style={{
            bottom: `calc(env(safe-area-inset-bottom, 0px) + ${isLandscape ? '16px' : '24px'})`,
            left: `calc(env(safe-area-inset-left, 0px) + 16px)`,
          }}
        >
          <Joystick
            joystickMaxRadius={50}
            joystickWrapperStyle={{ position: 'relative', height: '140px', width: '140px' }}
            joystickBaseStyle={{ width: '110px', height: '110px', background: 'rgba(20,14,8,0.55)', border: '2px solid rgba(190,150,80,0.6)' }}
            joystickKnobStyle={{ width: '54px', height: '54px', background: 'rgba(214,180,110,0.85)' }}
          />
        </div>
      )}
      
      {/* HUD Overlay for additional controls */}
      <div 
        className="norse-hud fixed inset-0 pointer-events-none z-30"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        data-testid="norse-mobile-controls"
      >
        {/* Bottom controls container */}
        <div 
          className={`absolute bottom-0 left-0 right-0 flex justify-between items-end p-4 ${isLandscape ? 'pb-4' : 'pb-6'}`}
          style={{ paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${isLandscape ? '16px' : '24px'})` }}
        >
          {/* Left side spacer (joystick handled separately) */}
          <div className="w-32" />
          
          {/* Right side controls */}
          <div className="flex flex-col items-end gap-3 pointer-events-auto">
            {/* Contextual Interact Button - Above action buttons */}
            {currentInteraction && !disabled && (
              <button
                className="norse-interact-btn px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-lg animate-pulse"
                onTouchStart={handleInteract}
                style={{ touchAction: 'none' }}
                data-norse-control="interact"
                data-testid="norse-interact-btn"
              >
                <Hand className="w-4 h-4 text-amber-100" />
                <span className="text-amber-100 text-sm font-semibold">{currentInteraction.prompt}</span>
              </button>
            )}
            
            {/* PTT Button */}
            {showPTT && (
              <button
                onTouchStart={handlePTTStart}
                onTouchEnd={handlePTTEnd}
                onTouchCancel={handlePTTEnd}
                disabled={micMuted || disabled}
                className={`norse-ptt-btn w-14 h-14 rounded-full flex flex-col items-center justify-center transition-all duration-150 shadow-lg select-none ${
                  micMuted
                    ? 'bg-red-900/80 border-red-700'
                    : isTransmitting 
                      ? 'bg-emerald-600 scale-110 shadow-emerald-500/50 border-emerald-400' 
                      : isConnecting
                        ? 'bg-amber-800/80 border-amber-600'
                        : isConnected
                          ? 'bg-stone-800/90 border-bronze'
                          : 'bg-stone-700/80 border-stone-500'
                }`}
                style={{ touchAction: 'none' }}
                data-norse-control="ptt"
                data-testid="norse-ptt-btn"
              >
                {isConnecting ? (
                  <Loader className="w-5 h-5 text-amber-200 animate-spin" />
                ) : micMuted ? (
                  <MicOff className="w-5 h-5 text-red-300" />
                ) : isTransmitting ? (
                  <Radio className="w-5 h-5 text-white animate-pulse" />
                ) : (
                  <Mic className="w-5 h-5 text-amber-200/70" />
                )}
                {!micMuted && !isConnecting && (
                  <span className="text-[9px] text-amber-200/60 mt-0.5">
                    {isTransmitting ? 'LIVE' : 'PTT'}
                  </span>
                )}
              </button>
            )}
            
            {/* Action Buttons using ecctrl VirtualButton.
                VirtualButton renders `label` inside its own cap — it does not render children. */}
            {!disabled && (
              <div className="flex items-center gap-2" data-testid="norse-action-buttons">
                <span data-norse-control="sprint" data-testid="norse-sprint-btn">
                  <VirtualButton
                    id={BTN_RUN}
                    label="RUN"
                    buttonWrapperStyle={NORSE_BTN_WRAPPER}
                    buttonCapStyle={{ ...NORSE_BTN_CAP, color: '#fbbf24' }}
                  />
                </span>

                <span data-norse-control="jump" data-testid="norse-jump-btn">
                  <VirtualButton
                    id={BTN_JUMP}
                    label="JUMP"
                    buttonWrapperStyle={NORSE_BTN_WRAPPER}
                    buttonCapStyle={{ ...NORSE_BTN_CAP, color: '#7dd3fc' }}
                  />
                </span>

                <span data-norse-control="action" data-testid="norse-action-btn">
                  <VirtualButton
                    id={BTN_ACTION}
                    label="ACT"
                    buttonWrapperStyle={NORSE_BTN_WRAPPER}
                    buttonCapStyle={{ ...NORSE_BTN_CAP, color: '#fca5a5' }}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Detect if device is mobile
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);
  
  return isMobile;
}
